import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useNavigation } from './navigation';
import { useSwrMutation } from './swr';
import { User } from '@/api/auth';
import { GameV1 } from '@/api/games';
import { userLimits } from '@/api/profile';
import { snakify } from '@/helpers/common';
import { isLimitReached } from '@/helpers/limits';
import { Modals } from '@/helpers/modals';
import useEstonia from '@/hooks/estonia';
import { useCommonStore, useModalsStore } from '@/store';

export enum PlayerFields {
  GENDER = 'gender',
}

export const isPlayerFieldsFilled = (
  user: User,
  propertiesToCheck: (keyof User)[]
) => {
  const missingFields: (keyof User)[] = [];

  for (const property of propertiesToCheck) {
    if (!user[property]) {
      missingFields.push(property);
    }
  }

  return {
    isAllFieldsFilled: missingFields.length === 0,
    missingFields,
  };
};

export function useGameRedirect(gameProvider?: GameV1['provider']) {
  const { isAuthenticated, user, playerFields } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      playerFields: state.playerFields,
    }))
  );
  const open = useModalsStore.use.open();
  const [inProcessRedirect, setInProcessRedirect] = useState(true);

  const { isEstonia } = useEstonia();

  const { navigate } = useNavigation();
  const { push } = useRouter();
  const pathname = usePathname();

  const {
    trigger: fetchUserLimits,
    isMutating: userLimitsPending,
    data: userLimitsData,
  } = useSwrMutation({
    key: 'userLimits',
    fetcher: userLimits,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchUserLimits();
  }, [fetchUserLimits, isAuthenticated]);

  useEffect(() => {
    if (userLimitsPending || !userLimitsData) return;
    if (!gameProvider || !playerFields || !isAuthenticated || !user) {
      setInProcessRedirect(false);
      return;
    }

    const { contexts } = playerFields;

    const { isAllFieldsFilled, missingFields } = isPlayerFieldsFilled(
      // @ts-ignore
      snakify(user),
      contexts[gameProvider] || []
    );

    const isMissingGender = missingFields.includes(PlayerFields.GENDER);

    const isLimitedAccess = isLimitReached(userLimitsData, user.currency);

    if (isAllFieldsFilled && !isLimitedAccess) {
      if (isEstonia && !user.sumsubVerified) {
        navigate('/profile-custom/verification');
        return;
      }
      setInProcessRedirect(false);
      return;
    }

    if (!isAllFieldsFilled || isLimitedAccess) {
      const limitsLink = `limits${isLimitedAccess ? '?action=limitedAccess' : ''}`;
      const personalLink = `personal?back-url=${encodeURIComponent(pathname)}`;

      if (isMissingGender && missingFields.length === 1) {
        open(Modals.GenderSelect, {
          customOnClose: () => {
            push(pathname);
          },
        });
        return;
      }
      navigate(
        `/profile-custom/${isLimitedAccess ? limitsLink : personalLink}`,
        true
      );
      return;
    }

    setInProcessRedirect(false);
  }, [
    gameProvider,
    isAuthenticated,
    navigate,
    playerFields,
    user,
    pathname,
    isEstonia,
    userLimitsPending,
    userLimitsData,
    open,
    push,
  ]);

  return inProcessRedirect;
}
